import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import './index.css';

// Import analytics and performance monitoring
import { analytics } from './lib/analytics';

// Initialize performance monitoring
if ('performance' in window) {
  // Monitor Core Web Vitals
  window.addEventListener('load', () => {
    // Track page load performance
    const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (perfData) {
      const loadTime = perfData.loadEventEnd - perfData.fetchStart;
      analytics.trackPerformance('page_load_time', loadTime);
    }
  });

  // Monitor largest contentful paint
  if ('PerformanceObserver' in window) {
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          analytics.trackPerformance('largest_contentful_paint', lastEntry.startTime);
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

      // Monitor first input delay
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const firstEntry = entries[0] as any;
        if (firstEntry && firstEntry.processingStart) {
          const fid = firstEntry.processingStart - firstEntry.startTime;
          analytics.trackPerformance('first_input_delay', fid);
        }
      });
      fidObserver.observe({ type: 'first-input', buffered: true });

      // Monitor cumulative layout shift
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        }
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });

      // Report CLS after 5 seconds
      setTimeout(() => {
        analytics.trackPerformance('cumulative_layout_shift', clsValue);
      }, 5000);
    } catch (error) {
      console.warn('Performance Observer not supported:', error);
    }
  }
}

// Global error handler
window.addEventListener('error', (event) => {
  analytics.trackError(new Error(event.message), {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    source: 'window.error'
  });
});

// Unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  analytics.trackError(new Error(`Unhandled Promise Rejection: ${event.reason}`), {
    source: 'unhandledrejection',
    reason: event.reason
  });
});

// Memory monitoring (if supported)
if ('memory' in performance) {
  setInterval(() => {
    const memory = (performance as any).memory;
    const usagePercentage = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
    
    if (usagePercentage > 80) {
      analytics.trackEvent('high_memory_usage', {
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
        usagePercentage
      });
    }
  }, 30000); // Check every 30 seconds
}

// Track initial page view
analytics.trackPageView(window.location.pathname, document.title);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
